/**
 * Deep Earth Laboratory design tokens - the platform-agnostic source of truth.
 *
 * Everything here is a raw scalar: hex strings, numbers, font stacks. There is
 * deliberately no CSS, no `var(--...)` indirection and no Mantine type in this
 * file, because React Native can consume none of them.
 *
 * The split is:
 *   - this module        -> raw values, both platforms
 *   - src/theme/assay.tsx -> Mantine theme + CSS custom properties, web only
 *
 * Sizes are stored as **pixel numbers** and converted to rem for the web by
 * `toRem`. React Native takes the pixel numbers directly. `tokens.test.ts`
 * pins the generated rem strings to the literals the theme shipped with, so a
 * refactor here cannot silently move the web type scale.
 */

/** A Mantine-shaped 10-step colour ramp, expressed without importing Mantine. */
export type ColorTuple = readonly [
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
];

/** Ore - precious, earned, not decorative. */
export const ORE: ColorTuple = [
  '#fef9f0',
  '#faedc8',
  '#f2d98a',
  '#e8c355',
  '#e8b84b', // 4 - refined/polished (highlights)
  '#c4983a', // 5 - primary ore (core actions)
  '#9a7426', // 6 - darker ore
  '#6b4f18', // 7 - deep ore
  '#3d2d0e', // 8 - very dark ore
  '#1a1206', // 9 - near-black ore
];

/** Patina - copper oxidation, the accent of long exposure. */
export const PATINA: ColorTuple = [
  '#eef5f3',
  '#cfe8e3',
  '#9fd1c9',
  '#6dbcb2',
  '#46a89b',
  '#3a8c80', // 5 - primary patina
  '#2b6b61',
  '#1c4d44',
  '#10302a',
  '#071812',
];

/** Dark - warm earth tones, not neutral black. Index 7 is the body background. */
export const DARK: ColorTuple = [
  '#f0ead8', // 0 - text primary (warm cream)
  '#e0d4bc', // 1 - text secondary light
  '#c4b498', // 2 - warm tan
  '#9a8470', // 3 - medium earth
  '#6b5c3e', // 4 - dust (muted text)
  '#4a3d2a', // 5 - deep muted
  '#221e14', // 6 - card surface (slightly lighter than body)
  '#14110a', // 7 - body background
  '#0c0906', // 8 - input / deep background
  '#070503', // 9 - near black
];

/** The four brand anchors, named for use outside a full palette context. */
export const BRAND = {
  earth: '#14110a',
  ore: '#c4983a',
  cream: '#f0ead8',
  patina: '#3a8c80',
} as const;

/** Ore gold, identical in both colour schemes. */
export const ORE_CONSTANTS = {
  ore: '#c4983a',
  oreLight: '#e8b84b',
} as const;

/**
 * Oxide - the palette's one destructive colour.
 *
 * Rust rather than emergency red, so it belongs to Deep Earth instead of
 * shouting over it. Reserved for actions that cannot be undone; anything
 * merely cautionary should use ore.
 */
export const OXIDE = {
  danger: '#8c4a3f',
  dangerLight: '#b06557',
} as const;

/** Semantic colours that differ per colour scheme, as raw values. */
export interface SchemeTokens {
  bg: string;
  surface: string;
  surfaceRaised: string;
  surfaceHover: string;
  bgInput: string;
  textPrimary: string;
  textBody: string;
  textLabel: string;
  textMuted: string;
  oreBorder: string;
  oreBorderHover: string;
}

export const SCHEME: Record<'dark' | 'light', SchemeTokens> = {
  dark: {
    bg: '#14110a',
    surface: '#221e14',
    surfaceRaised: '#2a2518',
    surfaceHover: '#302b1c',
    bgInput: '#0c0906',
    textPrimary: '#f0ead8',
    textBody: '#e0d4bc',
    textLabel: '#9a8470',
    textMuted: '#6b5c3e',
    oreBorder: 'rgba(196, 152, 58, 0.12)',
    oreBorderHover: 'rgba(196, 152, 58, 0.25)',
  },
  light: {
    bg: '#f5f0e4',
    surface: '#ece5cc',
    surfaceRaised: '#e3d9b8',
    surfaceHover: '#d8cfb0',
    bgInput: '#faf7ed',
    textPrimary: '#1c1508',
    textBody: '#3d3220',
    textLabel: '#6b5a40',
    textMuted: '#9a8468',
    oreBorder: 'rgba(196, 152, 58, 0.22)',
    oreBorderHover: 'rgba(196, 152, 58, 0.45)',
  },
};

/**
 * Font stacks. The first family in each is the one the app actually loads -
 * web via Google Fonts, mobile via expo-font - and the rest are fallbacks.
 */
export const FONTS = {
  heading: '"Cormorant Garamond", "Cormorant", "Georgia", serif',
  body: '"DM Mono", "Menlo", "Monaco", "Consolas", monospace',
  mono: '"DM Mono", "Menlo", "Monaco", "Consolas", monospace',
} as const;

/** Families to load at startup, without fallbacks - what expo-font/@font-face need. */
export const FONT_FAMILIES = {
  heading: 'Cormorant Garamond',
  body: 'DM Mono',
} as const;

/** Root font size the rem conversion assumes. */
export const REM_BASE = 16;

/** Pixels -> rem string. Web only; React Native uses the pixel numbers directly. */
export function toRem(px: number): string {
  return `${px / REM_BASE}rem`;
}

type Scale<K extends string> = Readonly<Record<K, number>>;

/** Convert a whole pixel scale to rem in one go. */
export function scaleToRem<K extends string>(scale: Scale<K>): Record<K, string> {
  return Object.fromEntries(
    Object.entries(scale).map(([key, px]) => [key, toRem(px as number)])
  ) as Record<K, string>;
}

export const SPACING = { xs: 8, sm: 14, md: 22, lg: 36, xl: 56 } as const;

export const FONT_SIZES = { xs: 10, sm: 12, md: 13, lg: 15, xl: 17 } as const;

/** Corner radii in pixels. Usable as-is on both platforms. */
export const RADIUS = { xs: 1, sm: 2, md: 4, lg: 6, xl: 10 } as const;

export interface HeadingSize {
  fontSize: number;
  lineHeight: string;
  fontWeight: string;
}

export const HEADINGS: Record<'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6', HeadingSize> = {
  h1: { fontSize: 72, lineHeight: '1.0', fontWeight: '300' },
  h2: { fontSize: 48, lineHeight: '1.05', fontWeight: '300' },
  h3: { fontSize: 36, lineHeight: '1.15', fontWeight: '400' },
  h4: { fontSize: 28, lineHeight: '1.2', fontWeight: '400' },
  h5: { fontSize: 22, lineHeight: '1.3', fontWeight: '500' },
  h6: { fontSize: 18, lineHeight: '1.4', fontWeight: '500' },
};

export const HEADING_FONT_WEIGHT = '400';
